import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/Icon';

// How many modals are open. The app behind them is made inert (unclickable, unfocusable, hidden
// from assistive tech) while any is, and released when the last one closes.
let openModals = 0;

const focusableSelector = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
].join(',');

/**
 * Keeps keyboard focus inside a modal and hands it back afterwards: focus moves in on open (to an
 * element marked data-autofocus, else the first focusable one), Tab and Shift+Tab wrap at the
 * ends, Escape closes, and whatever had focus before the modal opened gets it back on close.
 */
export function useFocusTrap(onClose: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Held in a ref so a parent re-rendering with a new closure doesn't re-run the effect (which
  // would steal focus back to the first field on every keystroke).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const root = ref.current;
    if (!root) return;

    const app = document.getElementById('root');
    openModals++;
    app?.setAttribute('inert', '');

    const focusables = () => [...root.querySelectorAll<HTMLElement>(focusableSelector)]
      .filter(el => el.offsetParent !== null || el === document.activeElement);
    const initial = root.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0] ?? root;
    initial.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('keydown', onKeyDown);
      openModals--;
      if (openModals === 0) app?.removeAttribute('inert');
      if (previous && document.contains(previous)) previous.focus();
    };
  }, []);

  return ref;
}

/**
 * Accessible modal: labelled by its heading, focus trapped, Escape closes. Clicking the backdrop
 * deliberately does NOT close it — these dialogs hold half-filled forms, and a stray click
 * outside shouldn't throw the work away.
 */
export function Dialog({ title, onClose, children, footer, size, headerExtra, describedBy }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'lg';
  /** Rendered between the header and the body (e.g. a step indicator). */
  headerExtra?: ReactNode;
  describedBy?: string;
}) {
  const titleId = useId();
  const ref = useFocusTrap(onClose);

  return createPortal(
    <div className="dialog-scrim">
      <div
        ref={ref}
        className={size === 'lg' ? 'dialog lg' : 'dialog'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}>
        <div className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        {headerExtra}
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
